import './Companies.scss';

import EmptyList from 'Components/EmptyList/EmptyList';
import { Content, LpBox } from 'Constants/styles';
import { ReactComponent as DotsIcon } from 'assets/svg/dots.svg';
import React, { MouseEvent, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import { setMerchantDetails } from 'redux/Actions';
import { toCurrency } from 'utils/currency';
import useClickOutsideWithoutRef from 'utils/useClickOutsideWithoutRef';

import { ListHeaders } from './Constants';
import { MerchantAPIResponseType } from './types';

export type CompaniesProps = {
  merchantsList: MerchantAPIResponseType[];
};

const Companies = ({ merchantsList }: CompaniesProps): ReactElement => {
  const { activeMerchantId } = useSelector((state: ReduxStateType) => ({
    activeMerchantId: state.activeMerchantId,
  }));

  const dispatch: Dispatch<any> = useDispatch();

  const toggleDropdown = (e: MouseEvent<HTMLDivElement>, merchantId: string): void => {
    e.preventDefault();
    dispatch(setMerchantDetails(false, merchantId, '', null, ''));
  };

  const handleClickOutside = () => {
    dispatch(setMerchantDetails(false, '', '', null, ''));
  };

  useClickOutsideWithoutRef(handleClickOutside, ['item-actions-block', 'item-actions-list', 'dots-icon', 'link-btn']);

  return (
    <div className="companies-page">
      <Content>
        <LpBox>
          <div className="card items">
            <ul className="item-list striped">
              <li className="item item-list-header">
                <div className="item-row">
                  {ListHeaders.map((header, index) => (
                    <div key={index} className={`item-col item-col-header ${header.className}`}>
                      <span>{header.text}</span>
                    </div>
                  ))}
                  <div key={`actions`}></div>
                </div>
              </li>
              {merchantsList.length === 0 && <EmptyList />}
              {merchantsList.length > 0 &&
                merchantsList.map((merchant) => (
                  <li className="item active-item" key={merchant.merchantId}>
                    <div className="item-row item-row-reset-m">
                      <div className="item-col item-col-sales item-col-lgr">
                        <NavLink
                          className="link-btn nav-link"
                          title={!!merchant.brandName ? merchant.brandName : merchant.businessName}
                          to={`/admin-dashboard/merchants/${merchant.merchantId}/details`}
                        >
                          {!!merchant.brandName ? merchant.brandName : merchant.businessName}
                        </NavLink>
                      </div>
                      <div className="item-col item-col-sales item-col-lgr">
                        <a
                          href={merchant.website?.includes('http') ? merchant.website : `https://${merchant.website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="website"
                        >
                          {merchant.website}
                        </a>
                      </div>
                      <div className="item-col item-col-sales custom-col-xsr">
                        <div className="item text-left mt-1 pl-m slug" title={merchant.slug}>
                          {merchant.slug}
                        </div>
                      </div>
                      <div className="item-col item-col-sales item-col-lgr price">
                        <span className="price">
                          {toCurrency(
                            merchant.payplanLimit.maxPayPlanAmountUnconfirmedCustomers,
                            merchant.payplanLimit.currency,
                          )}
                        </span>
                      </div>
                      <div className="item-col item-col-sales item-col-lgr price">
                        <span className="price">
                          {toCurrency(
                            merchant.payplanLimit.maxPayPlanAmountConfirmedCustomers,
                            merchant.payplanLimit.currency,
                          )}
                        </span>
                      </div>
                      <div className="item-col fixed item-col-actions-dropdown">
                        <div className="item-actions-dropdown" onClick={(e) => toggleDropdown(e, merchant.merchantId)}>
                          <DotsIcon className="dots-icon" />
                        </div>
                        <div
                          className={`item-actions-block ${activeMerchantId === merchant.merchantId ? 'active' : ''}`}
                        >
                          <ul className="item-actions-list">
                            <li>
                              <NavLink
                                className="link-btn"
                                to={`/admin-dashboard/merchants/${merchant.merchantId}/details`}
                              >
                                Manage
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className="link-btn"
                                to={`/admin-dashboard/transactions?merchantId=${merchant.merchantId}&merchantName=${merchant.businessName}`}
                              >
                                List transactions
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className="link-btn"
                                to={`/admin-dashboard/overdue?merchantId=${merchant.merchantId}&merchantName=${merchant.businessName}`}
                              >
                                Overdue plans
                              </NavLink>
                            </li>
                            <li>
                              <a
                                className="link-btn"
                                target="_blank"
                                rel="noreferrer"
                                href={`https://dashboard.stripe.com/connect/accounts/${merchant.stripeAccountId}`}
                              >
                                Go to Stripe account
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </LpBox>
      </Content>
    </div>
  );
};

export default Companies;
